import React from "react";
import { VLYT } from "@/constants/testIds/vlyt";
import useReveal from "@/hooks/useReveal";

function useCountUp(target, active, duration = 1400) {
  const [val, setVal] = React.useState(0);
  React.useEffect(() => {
    if (!active) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return val;
}

export default function TheFriction() {
  const { ref, inView } = useReveal();
  const minutes = useCountUp(8, inView);

  return (
    <section
      ref={ref}
      className="relative py-28 md:py-40 border-b border-[color:var(--vlyt-border)]"
      data-testid={VLYT.friction.section}
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid lg:grid-cols-[1fr_1.3fr] gap-14 items-center">
        <div className={`reveal ${inView ? "in" : ""}`}>
          <div className="text-eyebrow mb-5">01 · The Friction</div>
          <h2 className="text-h1 font-display">
            Every day, a student loses{" "}
            <span className="text-lime">8 minutes</span> to a shuttle that never comes.
          </h2>
          <p className="mt-6 text-[color:var(--vlyt-muted)] text-[16px] max-w-xl">
            Multiply that by 15,000 students. The result is a systemic loss of over 1.5 hours of
            productivity every week for each student. Vlyt eliminates this &lsquo;anxiety tax&rsquo;
            by replacing transport noise with technical certainty.
          </p>
        </div>

        <div className={`reveal ${inView ? "in" : ""}`} style={{ transitionDelay: "120ms" }}>
          <div className="relative rounded-[24px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)] p-10 md:p-14 overflow-hidden">
            <div
              className="absolute -top-24 -right-24 h-64 w-64 rounded-full"
              style={{ background: "radial-gradient(closest-side, rgba(191,255,0,0.18), transparent 70%)" }}
            />
            <div
              data-testid={VLYT.friction.stat}
              className="font-display leading-none text-[112px] md:text-[168px] text-lime tabular-nums tracking-tighter"
            >
              {minutes}
              <span className="text-[color:var(--vlyt-purple-soft)]">.</span>
            </div>
            <div className="mt-2 font-mono text-[13px] uppercase tracking-[0.32em] text-[color:var(--vlyt-muted)]">
              Minutes lost · per pickup · every day
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                ["15k+", "students"],
                ["1.5hr", "lost weekly"],
                ["₦0", "value returned"],
              ].map(([k, v]) => (
                <div key={k} className="border-l border-[color:var(--vlyt-border)] pl-3">
                  <div className="text-h3 font-display">{k}</div>
                  <div className="text-[12px] font-mono uppercase tracking-widest text-[color:var(--vlyt-muted)]">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
